import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ArrowsClockwise, FloppyDisk, UploadSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import SupplementaryReferences from "./SupplementaryReferences";

export default function ReferenceLibrary() {
    const [list, setList] = useState([]);
    const [scope, setScope] = useState("smb");
    const [doc, setDoc] = useState(null);
    const [draft, setDraft] = useState("");
    const [dirty, setDirty] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showBundled, setShowBundled] = useState(false);

    const loadList = async () => {
        try {
            const { data } = await api.get("/admin/references");
            setList(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const loadDoc = async (s) => {
        setErr("");
        setMsg("");
        try {
            const { data } = await api.get(`/admin/references/${s}`);
            setDoc(data);
            setDraft(data.markdown || "");
            setDirty(false);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    useEffect(() => {
        loadList();
    }, []);
    useEffect(() => {
        loadDoc(scope);
    }, [scope]);

    const onUpload = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            setErr("Please choose a PDF file.");
            return;
        }
        setUploading(true);
        setErr("");
        setMsg("");
        try {
            const buf = await file.arrayBuffer();
            const { data } = await api.post("/admin/references/extract", buf, {
                headers: { "Content-Type": "application/pdf" },
                transformRequest: [(d) => d],
            });
            setDraft(data.markdown);
            setDirty(true);
            setMsg(`Extracted ${data.chars.toLocaleString()} characters from ${file.name}. Review below and click Publish to make it the active reference.`);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const onPublish = async () => {
        if (!draft.trim()) {
            setErr("Reference is empty.");
            return;
        }
        if (!window.confirm(`Publish this as the active "${scope.toUpperCase()}" reference?`)) return;
        setSaving(true);
        setErr("");
        setMsg("");
        try {
            const { data } = await api.post(`/admin/references/${scope}`, { markdown: draft });
            setMsg(`Published. ${data.chars.toLocaleString()} characters now active.`);
            await Promise.all([loadList(), loadDoc(scope)]);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const onReset = async () => {
        if (!window.confirm(`Reset "${scope.toUpperCase()}" to the bundled reference?`)) return;
        try {
            await api.delete(`/admin/references/${scope}`);
            setMsg("Reset to bundled reference.");
            await Promise.all([loadList(), loadDoc(scope)]);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const meta = list.find((x) => x.scope === scope);

    return (
        <div data-testid="reference-library">
            <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <div className="overline mb-2">Reference Library</div>
                    <h2 className="font-display text-3xl">Microsoft plan-comparison reference</h2>
                    <p className="text-sm text-[#c2c2c2] mt-2 max-w-2xl leading-relaxed">
                        The active reference is injected (read-only context) into every report Claude
                        generates. SMB is auto-used for assessments with ≤300 users; Enterprise is
                        used for everything above. Update it whenever Microsoft refreshes the tables.
                    </p>
                </div>
                <div className="flex gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    {list.map((r) => (
                        <button
                            key={r.scope}
                            onClick={() => setScope(r.scope)}
                            className={`p-3 min-w-[160px] text-left ${
                                scope === r.scope ? "bg-[#141414]" : "bg-[#0a0a0a] hover:bg-[#141414]"
                            }`}
                            data-testid={`ref-scope-${r.scope}`}
                        >
                            <div className="text-[12px] uppercase tracking-widest text-[#9a9a9a]">
                                {r.label}
                            </div>
                            <div className="font-mono text-base mt-1">
                                {r.chars.toLocaleString()} <span className="text-xs text-[#9a9a9a]">chars</span>
                            </div>
                            <div className="text-[13px] mt-1">
                                <span className={r.source === "custom" ? "text-[#00A896]" : "text-[#9a9a9a]"}>
                                    {r.source === "custom" ? "● Custom" : "○ Bundled"}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {msg && <div className="block-opportunity mb-3 text-sm" data-testid="ref-msg">{msg}</div>}
            {err && <div className="block-risk mb-3 text-sm" data-testid="ref-error">{err}</div>}

            {doc && (
                <>
                    {/* Provenance / source banner */}
                    <div className="border border-[#2a2a2a] bg-[#0f1a19] p-4 mb-4 text-[13px] leading-relaxed" data-testid="ref-provenance">
                        <div className="overline mb-2">Bundled source</div>
                        {doc.provenance && doc.provenance.source_pdf ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[#d4d4d4]">
                                <div><span className="text-[#9a9a9a]">PDF:</span> <span className="text-white font-mono text-[12px]">{doc.provenance.source_pdf}</span></div>
                                <div><span className="text-[#9a9a9a]">Vintage:</span> <span className="text-white">{doc.provenance.vintage}</span></div>
                                <div><span className="text-[#9a9a9a]">Uploaded by:</span> {doc.provenance.source_uploaded_by}</div>
                                <div><span className="text-[#9a9a9a]">Extracted:</span> {doc.provenance.extracted_at}</div>
                                {doc.provenance.summary && (
                                    <div className="md:col-span-2 mt-2 pt-2 border-t border-[#2a2a2a] text-[#c2c2c2]">
                                        {doc.provenance.summary}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-[#9a9a9a]">No provenance recorded for this scope.</div>
                        )}
                        <div className="mt-3 pt-3 border-t border-[#2a2a2a] text-[12px] text-[#9a9a9a]">
                            <strong className="text-white">Important:</strong> the bundled reference is a static, point-in-time snapshot of the PDF above. It is <strong>not</strong> a live feed from Microsoft. Update it from <em>Admin → References → Upload PDF</em> when Microsoft publishes a refresh.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-4">
                        <div className="bg-[#0a0a0a] p-4">
                            <div className="overline">Active source</div>
                            <div className="font-display text-lg mt-1">
                                {doc.source === "custom" ? "Custom (uploaded)" : "Bundled"}
                            </div>
                        </div>
                        <div className="bg-[#0a0a0a] p-4">
                            <div className="overline">Last updated</div>
                            <div className="text-sm text-[#d4d4d4] mt-2">
                                {doc.updated_at
                                    ? new Date(doc.updated_at).toLocaleString()
                                    : "—"}
                            </div>
                        </div>
                        <div className="bg-[#0a0a0a] p-4 flex flex-wrap gap-2 items-center justify-end">
                            <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                                <UploadSimple size={14} weight="bold" />
                                {uploading ? "Extracting…" : "Upload PDF"}
                                <input
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    className="hidden"
                                    disabled={uploading}
                                    onChange={(e) => onUpload(e.target.files?.[0])}
                                    data-testid="ref-upload-input"
                                />
                            </label>
                            {meta?.source === "custom" && (
                                <button
                                    className="btn-ghost inline-flex items-center gap-2"
                                    onClick={onReset}
                                    data-testid="ref-reset-button"
                                >
                                    <ArrowCounterClockwise size={14} weight="bold" /> Reset
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="border border-[#2a2a2a] bg-[#141414]">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a]">
                            <div className="text-xs uppercase tracking-widest text-[#c2c2c2]">
                                Markdown preview ·{" "}
                                <span className="font-mono text-white">
                                    {draft.length.toLocaleString()} chars
                                </span>
                            </div>
                            <div className="flex gap-2">
                                {dirty && (
                                    <button
                                        className="btn-ghost inline-flex items-center gap-1"
                                        onClick={() => loadDoc(scope)}
                                        data-testid="ref-discard"
                                    >
                                        <ArrowsClockwise size={14} /> Discard changes
                                    </button>
                                )}
                                <button
                                    className="btn-primary inline-flex items-center gap-2"
                                    disabled={saving || !dirty}
                                    onClick={onPublish}
                                    data-testid="ref-publish-button"
                                >
                                    <FloppyDisk size={14} weight="bold" />
                                    {saving ? "Publishing…" : "Publish"}
                                </button>
                            </div>
                        </div>
                        <textarea
                            value={draft}
                            onChange={(e) => {
                                setDraft(e.target.value);
                                setDirty(true);
                            }}
                            className="input-base font-mono text-xs w-full"
                            style={{
                                height: "60vh",
                                resize: "vertical",
                                border: 0,
                                background: "#0a0a0a",
                                color: "#d4d4d4",
                                lineHeight: 1.55,
                            }}
                            data-testid="ref-textarea"
                        />
                    </div>
                </>
            )}

            <SupplementaryReferences />
        </div>
    );
}
